import React, { useState } from 'react';
import {
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ShowChart,
  Receipt,
  Star as StarIcon,
  AccountBalance as HoldingsIcon,
  Add as ManualTradingIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';

interface BottomNavigationProps {
  currentTab: number;
  onTabChange: (value: number) => void;
}

// 更多菜單中的功能映射
const MORE_MENU_ITEMS = [
  { index: 1, label: '行情', icon: ShowChart },
  { index: 2, label: '關注', icon: StarIcon },
  { index: 6, label: '統計', icon: AssessmentIcon },
];

const ADMIN_MENU_ITEMS = [
  { index: 7, label: '帳戶管理', icon: SettingsIcon },
  { index: 8, label: '用戶管理', icon: PeopleIcon },
];

const BottomNavigation: React.FC<BottomNavigationProps> = ({ currentTab, onTabChange }) => {
  const { state } = useAppContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Check if user is admin
  const isAdmin = state.googleUser?.role === 'admin';

  if (!isMobile) return null;

  // 判斷當前是否在更多菜單中的某個頁面
  const isInMoreMenu = MORE_MENU_ITEMS.some(item => item.index === currentTab) ||
    (isAdmin && ADMIN_MENU_ITEMS.some(item => item.index === currentTab));

  // 處理底部導航切換
  const handleNavigationChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === -1) {
      // 點擊"更多"按鈕，展開菜單
      return;
    }
    onTabChange(newValue);
  };

  // 處理更多菜單開啟
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  // 處理更多菜單關閉
  const handleMoreClose = () => {
    setMoreMenuAnchor(null);
  };

  // 處理菜單項點擊
  const handleMenuItemClick = (index: number) => {
    onTabChange(index);
    handleMoreClose();
  };

  // 獲取底部導航的當前值（如果是更多菜單中的頁面，顯示-1）
  const getBottomNavValue = () => {
    if (isInMoreMenu) {
      return -1;
    }
    return currentTab;
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <MuiBottomNavigation
          value={getBottomNavValue()}
          onChange={handleNavigationChange}
          sx={{
            height: 70,
            '& .MuiBottomNavigationAction-root': {
              fontSize: '16px',
              fontWeight: 'bold',
              minWidth: 0,
              padding: '6px 0',
              '&.Mui-selected': {
                color: '#1565c0',
              },
            },
          }}
        >
          <BottomNavigationAction
            label="首頁"
            icon={<DashboardIcon sx={{ fontSize: 28 }} />}
            value={0}
            sx={{ 
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem'
              }
            }}
          />
          <BottomNavigationAction
            label="庫存"
            icon={<HoldingsIcon sx={{ fontSize: 28 }} />}
            value={3}
            sx={{ 
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem'
              }
            }}
          />
          <BottomNavigationAction
            label="紀錄"
            icon={<Receipt sx={{ fontSize: 28 }} />}
            value={4}
            sx={{ 
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem'
              }
            }}
          />
          <BottomNavigationAction
            label="交易"
            icon={<ManualTradingIcon sx={{ fontSize: 28 }} />}
            value={5}
            sx={{ 
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem'
              }
            }}
          />
          <BottomNavigationAction
            label="更多"
            icon={
              <Badge 
                variant="dot" 
                color="primary" 
                invisible={!isInMoreMenu}
              >
                <MoreIcon sx={{ fontSize: 28 }} />
              </Badge>
            }
            value={-1}
            onClick={handleMoreClick}
            sx={{ 
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem'
              }
            }}
          />
        </MuiBottomNavigation>
      </Paper>

      {/* 更多功能菜單 */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: 200,
            maxHeight: 400,
            mb: 1,
          }
        }}
      >
        {MORE_MENU_ITEMS.map((item) => {
          const Icon = item.icon;
          const isSelected = currentTab === item.index;
          
          return (
            <MenuItem
              key={item.index}
              onClick={() => handleMenuItemClick(item.index)}
              selected={isSelected}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(21, 101, 192, 0.1)',
                  color: '#1565c0',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(21, 101, 192, 0.15)',
                },
              }}
            >
              <ListItemIcon sx={{ color: isSelected ? '#1565c0' : 'inherit' }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          );
        })}

        {isAdmin && [
          <MenuItem key="admin-divider" disabled sx={{ opacity: 0.5 }}>
            <ListItemText 
              primary="管理功能" 
              primaryTypographyProps={{ 
                variant: 'caption',
                fontWeight: 'bold'
              }} 
            />
          </MenuItem>,
          ...ADMIN_MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isSelected = currentTab === item.index;
            
            return (
              <MenuItem
                key={item.index}
                onClick={() => handleMenuItemClick(item.index)}
                selected={isSelected}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(21, 101, 192, 0.1)',
                    color: '#1565c0',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(21, 101, 192, 0.15)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: isSelected ? '#1565c0' : 'inherit' }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            );
          })
        ]}
      </Menu>
    </>
  );
};

export default BottomNavigation;
